import type { AnsweredBy } from '../../../utils/badgeStyles';

export interface GQLCallSummary {
  callId: string;
  contactId: string | null;
  contactName: string | null;
  conversationId: string | null;
  direction: string | null;
  inbound: boolean | null;
  fromNumber: string | null;
  toNumber: string | null;
  status: string | null;
  outcome: string | null;
  answeredBy: string | null;
  durationSeconds: number | null;
  initiatedAt: string | null;
  endedAt: string | null;
  summary: string | null;
  transcript: string | null;
  sentiment: string | null;
  recordingUrl: string | null;
  agentId: string | null;
  flowName: string | null;
  flowType: string | null;
  callAnalysisTimeline: string | null;
  callAnalysisAnswers: string | null;
  isTest: boolean | null;
  createdAt: string | null;
}

// Call shape expected by CallDetailsPanel and the flow Activity table
export interface GQLCall extends Record<string, unknown> {
  id: string;
  call_id: string;
  inbound?: boolean;
  answered_by?: AnsweredBy;
  callTimestamp?: string;
  to_number?: string;
  from_number?: string;
  recordingUrl?: string;
  status?: string;
  duration?: number;
  corrected_duration?: number;
  flowName?: string;
  flowType?: string;
  contactId?: string;
  contact?: undefined;
  fullName?: string;
  call_analysis?: {
    outcome: string;
    notes?: string;
    timeline?: Array<{ event: string; timestamp: string }>;
    answers?: Record<string, string | undefined>;
    summary?: string;
  };
  concatenated_transcript?: string;
  isTest?: boolean;
}

function tryParseJSON<T>(value: string | null | undefined, fallback: T): T {
  if (!value) return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

export function mapGQLCall(c: GQLCallSummary): GQLCall {
  const timeline = tryParseJSON<Array<{ event: string; timestamp: string }>>(
    c.callAnalysisTimeline,
    []
  );
  const answers = tryParseJSON<Record<string, string | undefined>>(c.callAnalysisAnswers, {});

  return {
    id: c.callId,
    call_id: c.callId,
    inbound: c.inbound ?? false,
    answered_by: (c.answeredBy ?? undefined) as AnsweredBy,
    callTimestamp: c.initiatedAt ?? undefined,
    to_number: c.toNumber ?? undefined,
    from_number: c.fromNumber ?? undefined,
    recordingUrl: c.recordingUrl ?? undefined,
    status: c.status ?? undefined,
    duration: c.durationSeconds ?? undefined,
    corrected_duration: c.durationSeconds ?? undefined,
    flowName: c.flowName ?? undefined,
    flowType: c.flowType ?? undefined,
    contactId: c.contactId ?? undefined,
    contact: undefined,
    fullName: c.contactName || c.toNumber || '-',
    concatenated_transcript: c.transcript ?? undefined,
    call_analysis: {
      outcome: c.outcome ?? '',
      timeline,
      answers,
      summary: c.summary ?? undefined,
    },
    isTest: c.isTest ?? false,
  };
}
